import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Card, Field, Select, Stack, Title, useToast } from "@introist/react-foundation/v2";
import styled from "styled-components";

import { TopBarRoute } from "components/templates";
import { SearchInput, Skeletons } from "components/molecules";
import { Empty } from "foundation";
import { useFeatureFlag } from "hooks";
import { useEmployees } from "modules/employees/hooks/useEmployees";
import { useWorkflows } from "modules/flows/hooks/useWorkflows";
import { createJourney } from "services/api/journeys/JourneyApi";
import {
  PerformanceReviewScenarioType,
  buildPerformanceReviewLaunchRequest,
  simulatePerformanceReviewRows,
  calculateAggregateStats
} from "../hooks/usePerformanceReviewData";
import { useWellbeingPreferences } from "modules/wellbeing/hooks/useWellbeingPreferences";
import { usePerformanceReviewConfig } from "../hooks/usePerformanceReviewConfig";
import { PerformanceReviewOverviewCards } from "../components/PerformanceReviewOverviewCards";
import { PerformanceReviewTable } from "../components/PerformanceReviewTable";

const scenarioTypeTitles: Record<PerformanceReviewScenarioType, string> = {
  NO_REVIEW_12M: "Overdue Review",
  NEW_MANAGER_NO_REVIEW: "New Manager — No Review",
  LONG_TENURE_NO_REVIEW: "Long Tenure — Never Reviewed",
  ROLE_CHANGE_REQUESTED: "Role Change Requested",
  FIRST_REVIEW_UPCOMING: "Probation Review Due"
};

const ControlsCard = styled(Card)`
  .pr-controls {
    display: grid;
    grid-template-columns: minmax(220px, 300px) minmax(220px, 260px) minmax(220px, 260px) 1fr;
    gap: var(--spacing-medium);
    align-items: end;
  }

  @media (max-width: 1100px) {
    .pr-controls {
      grid-template-columns: 1fr;
    }
  }
`;

export const PerformanceReviewRoute = () => {
  const toast = useToast();
  const hasPerformanceReviews = useFeatureFlag("performance-reviews").isEnabled;
  const { employees } = useEmployees({ fields: ["name"] });
  const { workflows } = useWorkflows({});
  const { selectedWorkflowId, setSelectedWorkflowId } = useWellbeingPreferences();
  const { config } = usePerformanceReviewConfig();

  const [search, setSearch] = useState("");
  const [signalFilter, setSignalFilter] = useState<PerformanceReviewScenarioType | "all">("all");
  const [severityFilter, setSeverityFilter] = useState<"all" | "medium" | "high">("all");
  const [launchingEmployeeId, setLaunchingEmployeeId] = useState<string | null>(null);

  useEffect(() => {
    if (!workflows || !selectedWorkflowId) return;
    if (!workflows.some(w => w.id === selectedWorkflowId)) {
      setSelectedWorkflowId(undefined);
    }
  }, [workflows, selectedWorkflowId, setSelectedWorkflowId]);

  const rows = useMemo(
    () => simulatePerformanceReviewRows(employees ?? [], undefined, undefined, config),
    [employees, config]
  );

  const aggregateStats = useMemo(
    () => calculateAggregateStats(employees ?? [], rows),
    [employees, rows]
  );

  const filteredRows = useMemo(() => {
    const lowerSearch = search.trim().toLowerCase();
    return rows.filter(row => {
      const matchSearch =
        lowerSearch.length === 0 || (row.employee.name ?? "").toLowerCase().includes(lowerSearch);
      const matchSignal =
        signalFilter === "all" || row.scenarios.some(s => s.type === signalFilter);
      const matchSeverity = severityFilter === "all" || row.topSeverity === severityFilter;
      return matchSearch && matchSignal && matchSeverity;
    });
  }, [rows, search, signalFilter, severityFilter]);

  const launchWorkflow = async (employeeId: string) => {
    if (!selectedWorkflowId) {
      toast.error("Select a workflow first");
      return;
    }
    setLaunchingEmployeeId(employeeId);
    try {
      const request = buildPerformanceReviewLaunchRequest(employeeId, selectedWorkflowId);
      const response = await createJourney(request);
      toast.success(`Automation started (${response.id})`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to start automation: ${errorMessage}`);
    } finally {
      setLaunchingEmployeeId(null);
    }
  };

  const workflowOptions = (workflows ?? []).map(w => ({ key: w.id, title: w.title }));

  if (!hasPerformanceReviews) {
    return <Navigate to="/employees" replace />;
  }

  if (!employees || !workflows) {
    return (
      <TopBarRoute paths={[{ key: "performance-reviews", title: "Performance Reviews" }]} medium>
        <Skeletons.List />
      </TopBarRoute>
    );
  }

  return (
    <TopBarRoute
      paths={[{ key: "performance-reviews", title: "Performance Reviews Dashboard" }]}
      medium
    >
      <Stack vertical gap="xLarge">
        <PerformanceReviewOverviewCards stats={aggregateStats} />

        <ControlsCard>
          <Stack vertical gap="small">
            <Title variant="bold">Employee Performance Review Signals</Title>
            <div className="pr-controls">
              <Field title="Workflow to launch">
                <Select
                  searchable
                  placeholder="Select workflow"
                  style={{ width: "100%" }}
                  options={workflowOptions}
                  value={selectedWorkflowId}
                  onSelect={opt => setSelectedWorkflowId(opt.key)}
                />
              </Field>
              <Field title="Signal">
                <Select
                  style={{ width: "100%" }}
                  options={[
                    { key: "all", title: "All signals" },
                    ...Object.entries(scenarioTypeTitles).map(([key, title]) => ({ key, title }))
                  ]}
                  value={signalFilter}
                  onSelect={opt =>
                    setSignalFilter(opt.key as PerformanceReviewScenarioType | "all")
                  }
                />
              </Field>
              <Field title="Severity">
                <Select
                  style={{ width: "100%" }}
                  options={[
                    { key: "all", title: "All severities" },
                    { key: "high", title: "High" },
                    { key: "medium", title: "Medium" }
                  ]}
                  value={severityFilter}
                  onSelect={opt => setSeverityFilter(opt.key as "all" | "medium" | "high")}
                />
              </Field>
              <SearchInput value={search} onChange={setSearch} />
            </div>
          </Stack>
        </ControlsCard>

        {filteredRows.length === 0 ? (
          <Empty title="No flagged employees" icon="personCheck" />
        ) : (
          <PerformanceReviewTable
            rows={filteredRows}
            selectedWorkflowId={selectedWorkflowId}
            onLaunchWorkflow={launchWorkflow}
            launchingEmployeeId={launchingEmployeeId}
          />
        )}
      </Stack>
    </TopBarRoute>
  );
};
